import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Inbox, LogOut, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pt-admin-token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  // login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(data);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const verify = async () => {
      if (!token) { setChecking(false); return; }
      try {
        await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setAuthed(true);
        await loadQuotes();
      } catch {
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } finally {
        setChecking(false);
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      const res = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${data.token}` } });
      setQuotes(res.data);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setQuotes([]);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-gold" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5">
        <form onSubmit={doLogin} data-testid="admin-login-form" className="w-full max-w-sm border border-white/10 bg-[#121212] p-8">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center bg-gold text-black"><Lock size={18} /></span>
            <div>
              <span className="block font-display text-xl">Owner Login</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-gold">Perfect Touch Admin</span>
            </div>
          </div>
          <label className="block mt-6 text-xs text-zinc-400">Email</label>
          <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full bg-[#0a0a0a] border border-white/15 text-white px-3 h-11 focus:border-gold outline-none"
            placeholder="you@email.com" />
          <label className="block mt-4 text-xs text-zinc-400">Password</label>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full bg-[#0a0a0a] border border-white/15 text-white px-3 h-11 focus:border-gold outline-none"
            placeholder="••••••••" />
          {err && <p data-testid="admin-error" className="mt-3 text-sm text-red-400">{err}</p>}
          <button type="submit" data-testid="admin-login-btn" disabled={submitting}
            className="mt-6 w-full bg-gold text-black font-semibold py-3 hover:bg-[#c5a059] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10 py-12">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[10px] uppercase tracking-[0.2em] text-gold">Perfect Touch · Admin</span>
            <h1 className="font-display text-4xl mt-2">Quote Requests</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={loadQuotes}
              className="flex items-center gap-2 border border-gold text-gold px-5 py-2.5 hover:bg-gold/10 transition-colors">
              <RefreshCw size={16} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout}
              className="flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2.5 hover:border-white/40 transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="flex items-center gap-3 text-zinc-400 py-20 justify-center">
              <Loader2 className="animate-spin" /> Loading…
            </div>
          ) : quotes.length === 0 ? (
            <div className="flex flex-col items-center text-zinc-500 py-24">
              <Inbox size={48} />
              <p className="mt-4">No quote requests yet.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-quote-list">
              {quotes.map((q) => (
                <div key={q.id} data-testid={`admin-quote-${q.id}`} className="border border-white/10 bg-[#121212] p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl">{q.name}</span>
                      <span className="text-[10px] uppercase tracking-[0.16em] text-gold border border-gold/40 px-2 py-1">
                        {q.service_type}
                      </span>
                    </div>
                    <span className="text-xs text-zinc-500">{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-4 grid gap-2 md:grid-cols-2 text-sm text-zinc-300">
                    <p><span className="text-zinc-500">Email:</span> {q.email}</p>
                    <p><span className="text-zinc-500">Phone:</span> {q.phone}</p>
                    <p><span className="text-zinc-500">Detail:</span> {q.service_detail || "—"}</p>
                    <p><span className="text-zinc-500">Address:</span> {q.address || "—"}</p>
                    <p><span className="text-zinc-500">Preferred:</span> {q.preferred_date || "—"}</p>
                  </div>
                  {q.message && <p className="mt-3 text-sm text-zinc-400 border-t border-white/10 pt-3 whitespace-pre-line">{q.message}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
